import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TeamAApi, type LostItem, formatDate } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

/**
 * A 队 · 失物详情
 * 责任学生:张翰天
 */
export function TeamAItemDetail() {
  const { id } = useParams();
  const [item, setItem] = useState<LostItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    TeamAApi.getItem(id)
      .then(setItem)
      .catch((e) => setError(e?.message ?? '加载失败'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-xs text-slate-500">失物详情加载中…</p>;
  if (error) return <p className="text-xs text-teamA-danger">{error}</p>;
  if (!item) return <p className="text-xs text-slate-500">失物不存在或已删除</p>;

  const f = item.features as {
    color?: string;
    shape?: string;
    text_mark?: string;
    pattern?: string;
    size?: string;
    notes?: string[];
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">失物详情</h1>
        <p className="text-xs text-slate-500">{formatDate(item.publishedAt)} 发布</p>
      </header>

      <section className="card space-y-3">
        {(() => {
          if (!item.imageUrl && !item.imageKey) {
            return (
              <div className="flex h-40 items-center justify-center rounded-md bg-slate-100 text-sm text-slate-400">
                暂无照片
              </div>
            );
          }
          return (
            <SafeImage
              team="a"
              imageUrl={item.imageUrl}
              imageKey={item.imageKey}
              alt={item.title}
              className="h-40 w-full rounded-md object-cover ring-1 ring-slate-200"
              placeholderClassName="flex h-40 w-full items-center justify-center rounded-md bg-slate-100 text-sm text-slate-400"
            />
          );
        })()}
        <div className="flex flex-wrap items-center gap-2">
          <span className="badge bg-teamA-primary/10 text-teamA-primary">{item.category}</span>
          {item.status === 'returned' ? (
            <span className="badge bg-teamA-safe/10 text-teamA-safe">已归还</span>
          ) : (
            <span className="badge bg-teamA-warning/10 text-teamA-warning">待认领</span>
          )}
          {item.privacyFlag && (
            <span className="badge bg-teamA-danger/10 text-teamA-danger">含敏感信息(已提醒)</span>
          )}
        </div>
        <h2 className="text-base font-semibold">{item.title}</h2>
        {item.description && <p className="text-sm text-slate-600">{item.description}</p>}

        <div className="rounded-md bg-slate-50 p-3 text-xs">
          <p className="mb-1 font-semibold text-slate-700">AI 提取的特征</p>
          <ul className="list-inside list-disc space-y-0.5 text-slate-600">
            {f.color && <li>主色:{f.color}</li>}
            {f.shape && <li>形状:{f.shape}</li>}
            {f.text_mark && <li>文字标记:{f.text_mark}</li>}
            {f.pattern && <li>图案:{f.pattern}</li>}
            {f.size && <li>相对大小:{f.size}</li>}
            {f.notes && f.notes.length > 0 && <li>其它:{f.notes.join(';')}</li>}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md bg-slate-50 p-2">
            <p className="text-slate-400">捡到地点</p>
            <p className="text-slate-700">{item.foundLocation || '未填'}</p>
          </div>
          <div className="rounded-md bg-slate-50 p-2">
            <p className="text-slate-400">存放地点</p>
            <p className="text-slate-700">{item.storageLocation || '未填'}</p>
          </div>
        </div>

        <Link to="/search" className="btn-primary w-full">
          这好像是我丢的 → 智能寻找
        </Link>
        <Link to="/" className="btn-outline w-full">
          返回失物墙
        </Link>
      </section>
    </div>
  );
}
